import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, Home } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { getZones } from "@/lib/api";
import { DateRangePicker } from "@/components/DateRangePicker";
import { GuestsPicker } from "@/components/GuestsPicker";

export const SearchBar = ({ variant = "hero", initial = {} }) => {
  const { t } = useApp();
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [zone, setZone] = useState(initial.zone || "all");
  const [type, setType] = useState(initial.type || "all");
  const [guests, setGuests] = useState(initial.guests || 2);
  const [childAges, setChildAges] = useState(initial.children_ages || []);
  const [ci, setCi] = useState(initial.check_in || "");
  const [co, setCo] = useState(initial.check_out || "");

  useEffect(() => {
    getZones().then(setZones).catch(() => {});
  }, []);

  // Remember the visitor's last search (dates + guests) and pre-fill it on the next
  // visit, so families don't have to re-enter everything. Stored only in the browser.
  useEffect(() => {
    const hasInitial = initial && (initial.check_in || initial.zone || initial.children_ages);
    if (hasInitial) return;
    try {
      const saved = JSON.parse(localStorage.getItem("ubp:lastSearch") || "null");
      if (saved) {
        if (saved.zone) setZone(saved.zone);
        if (saved.type) setType(saved.type);
        if (saved.guests) setGuests(saved.guests);
        if (Array.isArray(saved.children_ages)) setChildAges(saved.children_ages);
        if (saved.check_in) setCi(saved.check_in);
        if (saved.check_out) setCo(saved.check_out);
      }
    } catch (e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => {
    e.preventDefault();
    try {
      localStorage.setItem("ubp:lastSearch", JSON.stringify({
        zone, type, check_in: ci, check_out: co, guests, children_ages: childAges,
      }));
    } catch (e) { /* ignore */ }
    const p = new URLSearchParams();
    if (zone !== "all") p.set("zone", zone);
    if (type !== "all") p.set("type", type);
    if (ci) p.set("check_in", ci);
    if (co) p.set("check_out", co);
    p.set("guests", guests);
    if (childAges.length) p.set("children_ages", childAges.join(","));
    navigate(`/stays?${p.toString()}`);
  };

  const light = variant === "hero";
  const fieldCls = "flex-1 min-w-[140px]";
  const today = new Date().toISOString().slice(0, 10);

  return (
    <form
      onSubmit={submit}
      data-testid="hero-search-form"
      className={`flex flex-col gap-3 rounded-2xl p-3 md:flex-row md:items-stretch ${
        light ? "glass border border-white/40 shadow-2xl" : "border border-sand-border bg-sand-card"
      }`}
    >
      <label className={`${fieldCls} flex items-center gap-3 rounded-xl bg-white/70 px-4 py-3`}>
        <MapPin size={18} className="text-terracotta" />
        <span className="flex flex-col text-left">
          <span className="overline text-petrol/50" style={{ fontSize: "0.6rem" }}>
            {t("search.zone")}
          </span>
          <select
            data-testid="search-input-zone"
            value={zone}
            onChange={(e) => setZone(e.target.value)}
            className="bg-transparent text-sm font-semibold text-petrol outline-none"
          >
            <option value="all">{t("search.all_zones")}</option>
            {zones.map((z) => (
              <option key={z.slug} value={z.slug}>
                {z.name}
              </option>
            ))}
          </select>
        </span>
      </label>

      <label className={`${fieldCls} flex items-center gap-3 rounded-xl bg-white/70 px-4 py-3`}>
        <Home size={18} className="text-terracotta" />
        <span className="flex flex-col text-left">
          <span className="overline text-petrol/50" style={{ fontSize: "0.6rem" }}>
            {t("search.type")}
          </span>
          <select
            data-testid="search-input-type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="bg-transparent text-sm font-semibold text-petrol outline-none"
          >
            <option value="all">{t("search.all_types")}</option>
            <option value="hotel">{t("search.hotel")}</option>
            <option value="apartment">{t("search.apartment")}</option>
          </select>
        </span>
      </label>

      <DateRangePicker
        ci={ci}
        co={co}
        onChange={(a, b) => {
          setCi(a);
          setCo(b);
        }}
        triggerClassName="flex flex-1 min-w-[180px] items-center gap-3 rounded-xl bg-white/70 px-4 py-3 text-left"
      />

      <GuestsPicker
        adults={guests}
        childrenAges={childAges}
        onChange={(a, kids) => { setGuests(a); setChildAges(kids); }}
        light={light}
        triggerClassName="flex items-center gap-3 rounded-xl bg-white/70 px-4 py-3 md:w-52"
      />

      <button
        type="submit"
        data-testid="search-submit-button"
        className="flex items-center justify-center gap-2 rounded-xl bg-terracotta px-6 py-4 font-semibold text-white transition-colors hover:bg-terracotta-hover"
      >
        <Search size={18} />
        {t("search.submit")}
      </button>
    </form>
  );
};
